import * as Sentry from '@sentry/browser';
import { ApolloClient, DeviceInterface } from '@sfstudios/shapeshifter';
import { useEffect, useRef, useState } from 'react';
import { getClient } from '../utils/apolloClient';
import { useDevice } from '../utils/deviceInfo';
import { applyDeviceContext } from '../utils/sentryContext';
import { registerAllTrackers } from '../utils/trackers';

const BOOT_FAILED_TIMEOUT = 15000;

let APOLLO_CLIENT: ApolloClient | null = null;
// Synchronous re-entry guard. `useDevice()` returns a fresh `device` object on
// every render, so the effect deps below change every render and the effect
// re-fires. Without this flag, every re-render between mount and `getClient`
// resolving would start a concurrent boot — multiple `getClient` calls,
// `applyDeviceContext` running N times, etc.
let BOOT_IN_FLIGHT = false;

type BootStep =
  | 'init'
  | 'device-loading'
  | 'no-device'
  | 'apply-device-context'
  | 'get-apollo-client'
  | 'register-trackers'
  | 'ready';

type SetupDeviceAndClient =
  | {
      client: ApolloClient;
      device: DeviceInterface;
      ready: true;
    }
  | {
      client: null;
      device: null;
      ready: false;
    };

export const useSetupDeviceAndClient = (): SetupDeviceAndClient => {
  const { loading: deviceLoading, device } = useDevice();
  const [ready, setReady] = useState(false);

  const bootStep = useRef<BootStep>('init');
  const deviceLoadingRef = useRef(deviceLoading);
  const hasDeviceRef = useRef(device !== null);
  deviceLoadingRef.current = deviceLoading;
  hasDeviceRef.current = device !== null;

  useEffect(() => {
    if (deviceLoading || !device) {
      bootStep.current = deviceLoading ? 'device-loading' : 'no-device';
      return;
    }
    if (APOLLO_CLIENT !== null || BOOT_IN_FLIGHT) return;
    BOOT_IN_FLIGHT = true;

    (async () => {
      try {
        bootStep.current = 'apply-device-context';
        applyDeviceContext(device);

        bootStep.current = 'get-apollo-client';
        APOLLO_CLIENT = await getClient(device);

        bootStep.current = 'register-trackers';
        registerAllTrackers(APOLLO_CLIENT);

        bootStep.current = 'ready';
        setReady(true);
      } catch (e) {
        console.error(e);
        Sentry.withScope((scope) => {
          scope.setTag('boot_step', bootStep.current);
          Sentry.captureException(e);
        });
      } finally {
        BOOT_IN_FLIGHT = false;
      }
    })();
  }, [device, deviceLoading]);

  // Allow some time to pass before we report boot failure
  const timeout = useRef<number | undefined>(undefined);
  useEffect(() => {
    timeout.current = setTimeout(() => {
      const msg = `Boot failed: Failed to setup device, trackers and client.`;
      if (process.env.NODE_ENV === 'development') console.error(msg);

      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('boot_step', bootStep.current);
        scope.setContext('boot', {
          step: bootStep.current,
          deviceLoading: deviceLoadingRef.current,
          hasDevice: hasDeviceRef.current,
          timeoutMs: BOOT_FAILED_TIMEOUT
        });
        Sentry.captureMessage(msg);
      });
    }, BOOT_FAILED_TIMEOUT);

    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  useEffect(() => {
    if (!ready || APOLLO_CLIENT === null || device === null) {
      return;
    }

    clearTimeout(timeout.current);
  }, [device, ready]);

  if (!ready || APOLLO_CLIENT === null || device === null) {
    return {
      client: null,
      device: null,
      ready: false
    };
  }

  return {
    client: APOLLO_CLIENT,
    device,
    ready
  };
};
